import { Autocomplete, TextField } from "@mui/material";
import { FilterType, FilterTypes, MatchType, FileOptionTypes } from "../../../utilities/types";

// Define conditional filter options based on geometry types
interface FilterTypeOption {
	type: FilterType;
	display: string;
}

const PolygonFilterTypes: FilterTypeOption[] = [
	{ type: "Boundary", display: FileOptionTypes.Boundary.label },
	{ type: "Zones", display: FileOptionTypes.Zones.label },
	{ type: "Exclude", display: "Exclude" },
];

const PointFilterTypes: FilterTypeOption[] = [
	{ type: "Centroids", display: FileOptionTypes.Centroids.label },
	{ type: "Cores", display: FileOptionTypes.Cores.label },
	{ type: "Exclude", display: "Exclude" },
];

export interface FilterParameters {
	filterType: FilterType | null;
	filterKeys: string[];
	matchType: MatchType | null;
	value: string | null;
}

interface GeoTypeSelectorDropdownProps {
	onFilterChange: React.Dispatch<React.SetStateAction<Record<number, FilterParameters>>>;
	geometryAnalysis?: "polygons" | "points" | "mixed" | "none";
	globalType?: FilterType | null;
}

export default function GeoTypeSelectorDropdown({
	onFilterChange,
	geometryAnalysis,
	globalType,
}: GeoTypeSelectorDropdownProps) {
	// Create fallback options for all FilterTypes
	const AllFilterTypeOptions: FilterTypeOption[] = FilterTypes.map((type) => ({
		type,
		display: FileOptionTypes[type as keyof typeof FileOptionTypes]?.label || type,
	}));

	// Determine which options to show based on geometry analysis
	const getFilterOptions = (): FilterTypeOption[] => {
		switch (geometryAnalysis) {
			case "polygons":
				return PolygonFilterTypes;
			case "points":
				return PointFilterTypes;
			case "mixed":
			case "none":
			default:
				return AllFilterTypeOptions;
		}
	};

	const filterOptions = getFilterOptions();

	const globalOption = globalType ? filterOptions.find((option) => option.type === globalType) || null : null;

	return (
		<Autocomplete
			style={{ width: "100%" }}
			value={globalOption}
			getOptionLabel={(option) => option.display}
			onChange={(_, value) => {
				if (value !== null) {
					onFilterChange({
						0: {
							filterType: value.type,
							filterKeys: [],
							matchType: null,
							value: "",
						},
					});
				} else {
					onFilterChange({});
				}
			}}
			renderInput={(params) => {
				return <TextField {...params} label="Select Type" variant="outlined" />;
			}}
			options={filterOptions}
			isOptionEqualToValue={(option, value) => option?.type === value?.type}
			clearOnEscape={false}
		/>
	);
}
